import styled, { keyframes } from "styled-components";
import { colors } from "../../../../styles/colors";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

export const TableContainer = styled.div`
  width: 100%;
  margin-bottom: 30px;
  animation: ${fadeIn} 0.6s ease-out;

  table {
    width: 100%;
    border-collapse: collapse;

    th {
      padding-bottom: 6px;
      animation: ${fadeIn} 0.8s ease-out;

      .header-icon {
        width: 14px;
        height: 14px;
        display: block;
        margin: 0 auto;
        animation: ${scaleIn} 0.6s ease-out;
      }
    }

    tbody tr {
      animation: ${fadeInUp} 0.6s ease-out;
      animation-fill-mode: both;

      &:nth-child(1) {
        animation-delay: 0.1s;
      }
      &:nth-child(2) {
        animation-delay: 0.2s;
      }
      &:nth-child(3) {
        animation-delay: 0.3s;
      }
      &:nth-child(4) {
        animation-delay: 0.4s;
      }
      &:nth-child(5) {
        animation-delay: 0.5s;
      }
      &:nth-child(6) {
        animation-delay: 0.6s;
      }
      &:nth-child(7) {
        animation-delay: 0.7s;
      }
      &:nth-child(8) {
        animation-delay: 0.8s;
      }
    }

    td {
      padding: 7px 12px;
      text-align: center;
      border-bottom: 1px solid ${colors.lightGray};

      .color-indicator {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        animation: ${scaleIn} 0.8s ease-out;
      }

      .table-cell {
        font-size: 10px;
        color: ${colors.darkBlue};
        text-align: center;

        &.source-cell {
          text-align: right;
          display: flex;
          align-items: center;
          gap: 8px;
        }
      }
    }
  }
`;

export const SkeletonContainer = styled.div`
  width: 100%;
  animation: ${fadeIn} 0.6s ease-out;

  table {
    width: 100%;
    border-collapse: collapse;

    th {
      padding-bottom: 6px;
      animation: ${fadeIn} 0.8s ease-out;

      .header-icon {
        width: 14px;
        height: 14px;
        display: block;
        margin: 0 auto;
        animation: ${scaleIn} 0.6s ease-out;
      }
    }

    td {
      padding: 7px 12px;
      text-align: center;
      border-bottom: 1px solid ${colors.lightGray};
    }
  }
`;

export const SkeletonRow = styled.tr<{ $delay: number }>`
  animation: ${fadeInUp} 0.6s ease-out;
  animation-fill-mode: both;
  animation-delay: ${({ $delay }) => $delay}s;

  td {
    padding: 7px 12px;
    text-align: center;
    border-bottom: 1px solid ${colors.lightGray};
  }
`;

export const SkeletonCell = styled.div<{ $width: string; $height: string }>`
  width: ${({ $width }) => $width};
  height: ${({ $height }) => $height};
  background: linear-gradient(
    90deg,
    ${colors.lightGray} 25%,
    ${colors.blueGray}20 50%,
    ${colors.lightGray} 75%
  );
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;
  border-radius: 4px;
  margin: 0 auto;
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.4) 50%,
      transparent 100%
    );
    animation: ${shimmer} 1.5s infinite;
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-8px);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.6;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.1);
    opacity: 0.3;
  }
`;

export const NoDataContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 40px;
  text-align: center;
  direction: rtl;
  background: linear-gradient(
    135deg,
    ${colors.lightGray} 0%,
    ${colors.lightBlueGrayBackground} 50%,
    ${colors.lightGray} 100%
  );
  border-radius: 16px;
  border: 1px solid ${colors.lightBlueGrayAlpha};
  box-shadow: 0 4px 20px ${colors.lightBlueGrayAlpha40},
    0 1px 3px ${colors.lightBlueGrayAlpha};
  animation: ${fadeIn} 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  min-height: 280px;
  margin-bottom: 30px;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      ${colors.lightBlueGrayAlpha40},
      transparent
    );
    animation: ${shimmer} 3s ease-in-out infinite;
  }

  &::after {
    content: "";
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle,
      ${colors.lightBlueGrayAlpha} 0%,
      transparent 70%
    );
    animation: ${float} 6s ease-in-out infinite;
    z-index: 0;
  }

  @media (max-width: 768px) {
    padding: 40px 20px;
    min-height: 240px;
  }
`;

export const NoDataIcon = styled.div`
  margin-bottom: 24px;
  animation: ${float} 3s ease-in-out infinite;
  position: relative;
  z-index: 2;
  opacity: 0.8;
  transition: all 0.3s ease;
  filter: drop-shadow(0 4px 12px ${colors.lightBlueGrayAlpha40});

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 120px;
    height: 120px;
    background: radial-gradient(
      circle,
      ${colors.lightBlueGrayAlpha} 0%,
      transparent 70%
    );
    border-radius: 50%;
    z-index: -1;
    animation: ${pulse} 2s ease-in-out infinite;
  }

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 80px;
    height: 80px;
    background: linear-gradient(
      135deg,
      ${colors.lightBlueGrayAlpha40} 0%,
      transparent 50%
    );
    border-radius: 50%;
    z-index: -1;
    animation: ${float} 4s ease-in-out infinite reverse;
  }

  &:hover {
    opacity: 1;
    transform: scale(1.05);
    filter: drop-shadow(0 6px 16px ${colors.lightBlueGrayAlpha40});
  }

  svg {
    filter: drop-shadow(0 2px 8px ${colors.lightBlueGrayAlpha});
    transition: all 0.3s ease;
  }

  @media (max-width: 768px) {
    margin-bottom: 20px;

    &::before {
      width: 100px;
      height: 100px;
    }

    &::after {
      width: 70px;
      height: 70px;
    }
  }
`;

export const NoDataText = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${colors.darkBlue};
  font-family: "Noto Sans Hebrew", sans-serif;
  margin: 0 0 12px 0;
  line-height: 1.4;
  letter-spacing: 0.2px;
  position: relative;
  z-index: 2;
  max-width: 320px;

  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 8px;
  }
`;

export const NoDataSubtext = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: ${colors.blueGray};
  font-family: "Noto Sans Hebrew", sans-serif;
  margin: 0;
  line-height: 1.5;
  letter-spacing: 0.1px;
  position: relative;
  z-index: 2;
  max-width: 280px;
  opacity: 0.8;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;
